/* eslint-disable @next/next/no-img-element */
'use client';
import { useEffect, useRef, useState } from 'react';
import styles from './ImageCarousel.module.scss';
import { ImageCarouselProps } from './types';

export function ImageCarousel({ imgs, title, side }: ImageCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState<boolean[]>(() => new Array(imgs.length).fill(false));
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);
  const shouldScroll = imgs.length > 1;

  useEffect(() => {
    imgRefs.current.forEach((img, i) => {
      if (img?.complete) {
        setLoaded(prev => {
          if (prev[i]) return prev;
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!shouldScroll) return;
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % imgs.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [imgs.length, shouldScroll]);

  const handleLoad = (i: number) => {
    setLoaded(prev => {
      if (prev[i]) return prev;
      const next = [...prev];
      next[i] = true;
      return next;
    });
  };

  return (
    <div className={styles.carousel}>
      <div
        className={styles.carousel_track}
        style={{ transform: `translateX(${side === 'left' ? '-' : ''}${current * 100}%)` }}
      >
        {imgs.map((img, i) => (
          <figure key={i} className={styles.carousel_slide}>
            {!loaded[i] && <div className={styles.carousel_skeleton} />}
            <img
              ref={el => { imgRefs.current[i] = el; }}
              src={img}
              alt={`${title} ${i + 1}`}
              onLoad={() => handleLoad(i)}
              onError={() => handleLoad(i)}
              style={{
                width: 'auto',
                height: '25dvw',
                objectFit: 'contain',
                border: 'var(--border-bold)',
                borderRadius: '1dvw',
                opacity: loaded[i] ? 1 : 0,
                transition: 'opacity 0.4s ease',
              }}
            />
          </figure>
        ))}
      </div>

      {shouldScroll && (
        <div className={styles.carousel_dots}>
          {imgs.map((_, i) => (
            <button
              key={i}
              className={`${styles.carousel_dots_dot} ${i === current ? styles.carousel_dots_dot__active : ''}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
